import { useState, useEffect } from 'react'
import { Modal, TextInput, Select, MultiSelect, Group, Avatar, Stack } from '@mantine/core'
import { IconDeviceFloppy } from '@tabler/icons-react'
import { useArcTranslation } from 'hooks'
import { Button } from 'components/Button'
import type { ManagedStudent, GradeOption } from 'api/arcMosaico/types'
import { getInitials } from 'helpers'

interface Props {
  student: ManagedStudent | null
  groups: Array<{ id: string; name: string }>
  grades: GradeOption[]
  onClose: () => void
  onSave: (data: {
    firstName: string; lastName: string; gradeId: string | null
    groupIds: string[]; username: string; email: string
  }) => Promise<void>
}

export function EditStudentModal({ student, groups, grades, onClose, onSave }: Props) {
  const { t } = useArcTranslation()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [gradeId, setGradeId] = useState<string | null>(null)
  const [groupIds, setGroupIds] = useState<string[]>([])
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)

  const opened = student !== null

  useEffect(() => {
    if (student) {
      setFirstName(student.firstName)
      setLastName(student.lastName)
      setUsername(student.username)
      setEmail(student.email || '')
      setGradeId(student.grades?.[0]?.id || null)
      setGroupIds(student.groups?.map(g => g.id) || [])
    }
  }, [student])

  const handleClose = () => {
    setFirstName('')
    setLastName('')
    setUsername('')
    setEmail('')
    setGradeId(null)
    setGroupIds([])
    onClose()
  }

  const handleSave = async () => {
    setLoading(true)
    try {
      await onSave({ firstName, lastName, gradeId, groupIds, username, email })
    } finally {
      setLoading(false)
    }
  }

  const groupOptions = groups.map(g => ({ value: g.id, label: g.name }))
  const gradeOptions = grades.map(g => ({ value: g.id, label: g.name }))

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('edit_student_modal.title')}
      size='md'
      centered
    >
      <Stack spacing='sm'>
        {student && (
          <Group position='center'>
            <Avatar src={student.avatarUrl} size={64} radius='xl' color='gray.5'>
              {getInitials(`${student.firstName} ${student.lastName}`)}
            </Avatar>
          </Group>
        )}

        <Group grow>
          <TextInput
            label={t('edit_student_modal.first_name')}
            value={firstName}
            onChange={e => setFirstName(e.currentTarget.value)}
            required
          />
          <TextInput
            label={t('edit_student_modal.last_name')}
            value={lastName}
            onChange={e => setLastName(e.currentTarget.value)}
            required
          />
        </Group>

        <Select
          label={t('edit_student_modal.grade_level')}
          data={gradeOptions}
          value={gradeId}
          onChange={setGradeId}
          placeholder={t('edit_student_modal.select_grade')}
          required
        />

        <MultiSelect
          label={t('edit_student_modal.groups')}
          data={groupOptions}
          value={groupIds}
          onChange={setGroupIds}
          placeholder={t('edit_student_modal.select_groups')}
          required
        />

        <Group grow>
          <TextInput
            label={t('edit_student_modal.username')}
            value={username}
            onChange={e => setUsername(e.currentTarget.value)}
            required
          />
          <TextInput
            label={t('edit_student_modal.email')}
            value={email}
            onChange={e => setEmail(e.currentTarget.value)}
          />
        </Group>

        <Group position='right' mt='md'>
          <Button variant='default' onClick={handleClose}>
            {t('edit_student_modal.cancel')}
          </Button>
          <Button
            color='turquoise'
            leftIcon={<IconDeviceFloppy size={16} />}
            onClick={handleSave}
            loading={loading}
          >
            {t('edit_student_modal.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
